<template>
  <v-container fluid class="pa-6">
    <v-overlay color="primary" :absolute="true" v-if="loading">
      <v-progress-circular
        indeterminate
        color="accent"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Settings
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Manage administrators, scoring, AI providers, data export, and platform configuration.
        </p>
      </v-col>
    </v-row>

    <v-tabs v-model="activeTab" color="primary" class="mt-2">
      <v-tab>Administrators</v-tab>
      <v-tab>User Management</v-tab>
      <v-tab>Intelligence Configuration</v-tab>
      <v-tab>Scoring & Hierarchy</v-tab>
      <v-tab>AI Providers</v-tab>
      <v-tab>Data Export & Cache</v-tab>
      <v-tab>Branding</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12">
            <v-card flat class="simpleCard">
              <v-card-title>Add New Administrator</v-card-title>
              <v-card-subtitle>
                Administrators are identified by email address across all login
                types
              </v-card-subtitle>
              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="8" md="6">
                    <v-text-field
                      v-model="newAdminEmail"
                      label="Email Address"
                      outlined
                      dense
                      :disabled="adding"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4" md="3">
                    <v-btn
                      color="primary"
                      @click="addAdmin"
                      :loading="adding"
                      :disabled="!newAdminEmail"
                    >
                      Add Admin
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard">
              <v-card-title>
                Current Administrators ({{ adminRoles.length || adminEmails.length }})
              </v-card-title>
              <v-card-text>
                <div
                  v-if="adminRoles.length === 0 && adminEmails.length === 0"
                  class="text-body-2 text--secondary"
                >
                  No admins configured.
                </div>
                <v-list v-else dense>
                  <v-list-item v-for="admin in adminRolesList" :key="admin.email">
                    <v-list-item-content>
                      <v-list-item-title class="d-flex align-center">
                        {{ admin.email }}
                        <v-chip
                          x-small
                          class="ml-2"
                          :color="roleChipColor(admin.role)"
                          text-color="white"
                        >
                          {{ roleLabel(admin.role) }}
                        </v-chip>
                        <v-chip x-small class="ml-1" color="grey darken-1" dark v-if="isCurrentUser(admin.email)">
                          You
                        </v-chip>
                      </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action class="d-flex flex-row align-center" style="gap: 4px">
                      <v-select
                        v-if="isSuperAdmin && !isCurrentUser(admin.email)"
                        :value="admin.role"
                        :items="roleOptions"
                        dense
                        outlined
                        hide-details
                        style="max-width: 150px"
                        @change="changeRole(admin.email, $event)"
                      />
                      <v-btn
                        icon
                        small
                        @click="removeAdmin(admin.email)"
                        :disabled="removing === admin.email || isCurrentUser(admin.email) || (!isSuperAdmin && admin.role === 'super_admin')"
                      >
                        <v-icon color="error">mdi-close</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3" style="color: var(--v-primary-base, #1E3A5F)">
                <v-icon left color="primary">mdi-palette</v-icon>
                Display
              </h3>
              <div class="text-body-2 mb-3">Choose your preferred appearance. "System" follows your device settings.</div>
              <v-btn-toggle
                :value="themePreference"
                mandatory
                dense
                color="primary"
                @change="handleThemeChange"
              >
                <v-btn value="light" small>
                  <v-icon left small>mdi-white-balance-sunny</v-icon>
                  Light
                </v-btn>
                <v-btn value="system" small>
                  <v-icon left small>mdi-monitor</v-icon>
                  System
                </v-btn>
                <v-btn value="dark" small>
                  <v-icon left small>mdi-moon-waning-crescent</v-icon>
                  Dark
                </v-btn>
              </v-btn-toggle>
              <div v-if="themeSaving" class="text-caption mt-2 grey--text">
                Saving...
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3" style="color: var(--v-primary-base, #1E3A5F)">
                <v-icon left color="primary">mdi-shield-lock</v-icon>
                Data Governance
              </h3>

              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="text-body-2 font-weight-medium mb-2">Data Retention Policy</div>
                  <div class="text-caption grey--text mb-2">
                    How long exported and cached profile data should be retained.
                  </div>
                  <v-select
                    v-model="dataRetentionDays"
                    :items="retentionOptions"
                    outlined
                    dense
                    color="#007AFF"
                    style="max-width: 250px"
                  />
                  <v-btn
                    small
                    color="#007AFF"
                    dark
                    :loading="savingRetention"
                    @click="saveRetentionPolicy"
                  >
                    Save Retention Policy
                  </v-btn>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-body-2 font-weight-medium mb-2">Current Settings</div>
                  <div class="d-flex align-center mb-1">
                    <v-icon small color="#007AFF" class="mr-2">mdi-calendar-clock</v-icon>
                    <span class="text-body-2">Retention: <strong>{{ dataRetentionDays }} days</strong></span>
                  </div>
                  <div class="d-flex align-center mb-1">
                    <v-icon small color="#10B981" class="mr-2">mdi-shield-check</v-icon>
                    <span class="text-body-2">Consent tracking: <strong>Active</strong></span>
                  </div>
                  <div class="d-flex align-center">
                    <v-icon small color="#8b5cf6" class="mr-2">mdi-clipboard-text-clock</v-icon>
                    <span class="text-body-2">Export audit: <strong>{{ exportAuditLog.length }} entries</strong></span>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <div class="d-flex align-center mb-3">
                <h4 class="text-subtitle-2 font-weight-bold theme-heading">
                  Export Audit Trail
                </h4>
                <v-spacer />
                <v-btn x-small text color="#007AFF" @click="loadExportAuditLog">
                  <v-icon x-small left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
              <v-simple-table v-if="exportAuditLog.length > 0" dense>
                <thead>
                  <tr>
                    <th>Admin</th>
                    <th>Export Type</th>
                    <th>Records</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in exportAuditLog.slice(0, 20)" :key="entry.id">
                    <td class="text-body-2">{{ entry.adminEmail }}</td>
                    <td>
                      <v-chip x-small color="#007AFF" text-color="white">{{ entry.exportType }}</v-chip>
                    </td>
                    <td class="text-body-2">{{ entry.recordCount }}</td>
                    <td class="text-caption grey--text">{{ formatAuditDate(entry.timestamp) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="text-center py-4 grey--text text-caption">
                No export activity recorded yet.
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 font-weight-bold theme-heading">
                  <v-icon left color="primary">mdi-shield-check</v-icon>
                  Allowlist
                </h3>
                <v-btn x-small text color="#007AFF" @click="loadAllowlist" :loading="allowlistLoading">
                  <v-icon x-small left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                Restrict platform access to specific emails or domains. When entries exist, only matching users can sign up.
              </div>

              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="allowlistEntryType"
                    :items="[{text: 'Email', value: 'email'}, {text: 'Domain (e.g. @company.com)', value: 'domain'}]"
                    label="Entry Type"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="allowlistEntryValue"
                    :label="allowlistEntryType === 'domain' ? 'Domain (e.g. @company.com)' : 'Email Address'"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-text-field
                    v-model="allowlistEntryReason"
                    label="Reason (optional)"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-btn
                    color="#007AFF"
                    dark
                    block
                    :disabled="!allowlistEntryValue"
                    :loading="allowlistAdding"
                    @click="addAllowlistEntry"
                  >
                    Add
                  </v-btn>
                </v-col>
              </v-row>

              <v-progress-linear v-if="allowlistLoading" indeterminate color="#007AFF" class="my-3" />
              <v-simple-table v-if="allowlistEntries.length > 0" dense class="mt-3">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Added By</th>
                    <th>Reason</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in allowlistEntries" :key="entry.id">
                    <td>
                      <v-chip x-small :color="entry.entryType === 'domain' ? '#8b5cf6' : '#007AFF'" text-color="white">
                        {{ entry.entryType }}
                      </v-chip>
                    </td>
                    <td class="text-body-2">{{ entry.email || entry.domain }}</td>
                    <td class="text-body-2">{{ entry.addedBy }}</td>
                    <td class="text-body-2">{{ entry.reason || '—' }}</td>
                    <td class="text-caption grey--text">{{ formatAuditDate(entry.addedAt) }}</td>
                    <td>
                      <v-btn icon x-small @click="removeAllowlistEntry(entry.id)" :loading="allowlistRemoving === entry.id">
                        <v-icon small color="error">mdi-close</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else-if="!allowlistLoading" class="text-center py-3 grey--text text-caption">
                No allowlist entries. All users can sign up.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 font-weight-bold theme-heading">
                  <v-icon left color="error">mdi-account-cancel</v-icon>
                  Blocklist
                </h3>
                <v-btn x-small text color="#007AFF" @click="loadBlocklist" :loading="blocklistLoading">
                  <v-icon x-small left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                Blocked users are prevented from logging in and excluded from all search results, directory listings, and candidate pools.
              </div>

              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="blocklistEmail"
                    label="Email Address"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="blocklistDisplayName"
                    label="Display Name (optional)"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="blocklistReason"
                    label="Reason"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-btn
                    color="error"
                    dark
                    block
                    :disabled="!blocklistEmail"
                    :loading="blocklistAdding"
                    @click="addBlocklistEntry"
                  >
                    Block
                  </v-btn>
                </v-col>
              </v-row>

              <v-progress-linear v-if="blocklistLoading" indeterminate color="error" class="my-3" />
              <v-simple-table v-if="blocklistEntries.length > 0" dense class="mt-3">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Display Name</th>
                    <th>Blocked By</th>
                    <th>Reason</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in blocklistEntries" :key="entry.id">
                    <td class="text-body-2">{{ entry.email }}</td>
                    <td class="text-body-2">{{ entry.displayName || '—' }}</td>
                    <td class="text-body-2">{{ entry.addedBy }}</td>
                    <td class="text-body-2">{{ entry.reason || '—' }}</td>
                    <td class="text-caption grey--text">{{ formatAuditDate(entry.addedAt) }}</td>
                    <td>
                      <v-btn icon x-small @click="removeBlocklistEntry(entry.id)" :loading="blocklistRemoving === entry.id">
                        <v-icon small color="success">mdi-account-check</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else-if="!blocklistLoading" class="text-center py-3 grey--text text-caption">
                No blocked users.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 font-weight-bold theme-heading">
                  <v-icon left color="warning">mdi-account-off</v-icon>
                  Deactivated Users
                </h3>
                <v-btn x-small text color="#007AFF" @click="loadDeactivatedUsers" :loading="deactivatedLoading">
                  <v-icon x-small left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                Deactivated users are hidden from all views but their data is preserved for potential reactivation.
              </div>

              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="deactivateEmail"
                    label="Email Address"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="deactivateDisplayName"
                    label="Display Name (optional)"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="deactivateReason"
                    label="Reason"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2">
                  <v-btn
                    color="warning"
                    dark
                    block
                    :disabled="!deactivateEmail"
                    :loading="deactivating"
                    @click="deactivateUserAction"
                  >
                    Deactivate
                  </v-btn>
                </v-col>
              </v-row>

              <v-progress-linear v-if="deactivatedLoading" indeterminate color="warning" class="my-3" />
              <v-simple-table v-if="deactivatedUsers.length > 0" dense class="mt-3">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Display Name</th>
                    <th>Deactivated By</th>
                    <th>Reason</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in deactivatedUsers" :key="entry.id">
                    <td class="text-body-2">{{ entry.email }}</td>
                    <td class="text-body-2">{{ entry.displayName || '—' }}</td>
                    <td class="text-body-2">{{ entry.deactivatedBy }}</td>
                    <td class="text-body-2">{{ entry.reason || '—' }}</td>
                    <td class="text-caption grey--text">{{ formatAuditDate(entry.deactivatedAt) }}</td>
                    <td>
                      <v-btn x-small color="success" dark @click="reactivateUserAction(entry.id)" :loading="reactivating === entry.id">
                        Reactivate
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else-if="!deactivatedLoading" class="text-center py-3 grey--text text-caption">
                No deactivated users.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 font-weight-bold theme-heading">
                  <v-icon left color="#8b5cf6">mdi-clipboard-text-clock</v-icon>
                  Admin Audit Log
                </h3>
                <v-btn x-small text color="#007AFF" @click="loadAuditLog" :loading="auditLogLoading">
                  <v-icon x-small left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                All administrative actions are logged for compliance and accountability.
              </div>

              <v-row dense class="mb-3">
                <v-col cols="12" sm="3">
                  <v-select
                    v-model="auditLogActionFilter"
                    :items="auditActionTypes"
                    label="Filter by Action"
                    outlined
                    dense
                    clearable
                    color="#007AFF"
                    hide-details
                    @change="loadAuditLog"
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="auditLogTargetFilter"
                    label="Filter by Target User"
                    outlined
                    dense
                    clearable
                    color="#007AFF"
                    hide-details
                    @keyup.enter="loadAuditLog"
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="auditLogStartDate"
                    label="Start Date"
                    type="date"
                    outlined
                    dense
                    clearable
                    color="#007AFF"
                    hide-details
                    @change="loadAuditLog"
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="auditLogEndDate"
                    label="End Date"
                    type="date"
                    outlined
                    dense
                    clearable
                    color="#007AFF"
                    hide-details
                    @change="loadAuditLog"
                  />
                </v-col>
              </v-row>

              <v-progress-linear v-if="auditLogLoading" indeterminate color="#8b5cf6" class="my-3" />
              <v-simple-table v-if="auditLogEntries.length > 0" dense>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Admin</th>
                    <th>Target</th>
                    <th>Details</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in auditLogEntries.slice(0, 50)" :key="entry.id">
                    <td>
                      <v-chip x-small :color="getAuditActionColor(entry.actionType)" text-color="white">
                        {{ formatAuditAction(entry.actionType) }}
                      </v-chip>
                    </td>
                    <td class="text-body-2">{{ entry.actingAdmin }}</td>
                    <td class="text-body-2">{{ entry.targetUser || '—' }}</td>
                    <td class="text-body-2" style="max-width: 300px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
                      {{ entry.details || '—' }}
                    </td>
                    <td class="text-caption grey--text">{{ formatAuditDate(entry.timestamp) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else-if="!auditLogLoading" class="text-center py-3 grey--text text-caption">
                No audit log entries found.
              </div>
              <div v-if="auditLogEntries.length > 50" class="text-caption grey--text font-italic pa-2 text-center">
                Showing 50 of {{ auditLogEntries.length }} entries
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 font-weight-bold theme-heading">
                  <v-icon left color="#007AFF">mdi-account-group</v-icon>
                  User Directory
                </h3>
                <div class="d-flex align-center">
                  <v-chip v-if="userDirectoryConflictCount > 0" small color="error" text-color="white" class="mr-2">
                    {{ userDirectoryConflictCount }} conflict{{ userDirectoryConflictCount > 1 ? 's' : '' }}
                  </v-chip>
                  <v-btn x-small text color="#007AFF" @click="loadUserDirectory" :loading="userDirectoryLoading">
                    <v-icon x-small left>mdi-refresh</v-icon>
                    Refresh
                  </v-btn>
                </div>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                All registered users and their current access status. Blocklist takes precedence over allowlist. Deactivation takes precedence over allowlist.
              </div>

              <v-row dense class="mb-3">
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="userDirectorySearch"
                    label="Search by name or email"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select
                    v-model="userDirectoryStatusFilter"
                    :items="[{text: 'All Statuses', value: ''}, {text: 'Active', value: 'active'}, {text: 'Blocked', value: 'blocked'}, {text: 'Deactivated', value: 'deactivated'}]"
                    label="Status"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-select
                    v-model="userDirectoryRoleFilter"
                    :items="[{text: 'All Roles', value: ''}, {text: 'Super Admin', value: 'super_admin'}, {text: 'Admin', value: 'admin'}, {text: 'User', value: 'user'}]"
                    label="Role"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="2" class="d-flex align-center">
                  <span class="text-caption grey--text">
                    {{ filteredUserDirectory.length }} of {{ userDirectoryEntries.length }}
                  </span>
                </v-col>
              </v-row>

              <v-alert v-if="userDirectoryConflicts.length > 0" type="warning" dense outlined class="mb-3">
                <strong>Conflicts detected:</strong> {{ userDirectoryConflicts.length }} user(s) have conflicting access settings.
                <div v-for="c in userDirectoryConflicts" :key="c.email" class="text-body-2 mt-1">
                  <strong>{{ c.name || c.email }}</strong>:
                  <span v-for="(w, i) in c.conflicts" :key="i">
                    {{ formatConflictType(w) }}<span v-if="i < c.conflicts.length - 1">, </span>
                  </span>
                </div>
              </v-alert>

              <v-progress-linear v-if="userDirectoryLoading" indeterminate color="#007AFF" class="my-3" />
              <v-simple-table v-if="filteredUserDirectory.length > 0" dense>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th>Login Provider</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in paginatedUserDirectory" :key="user.email"
                    :class="{ 'red lighten-5': user.conflicts && user.conflicts.length > 0 && !$vuetify.theme.dark, 'red darken-4': user.conflicts && user.conflicts.length > 0 && $vuetify.theme.dark }">
                    <td class="text-body-2">
                      {{ user.name || '—' }}
                      <v-icon v-if="user.conflicts && user.conflicts.length > 0" small color="warning" class="ml-1" @click="showUserConflictDetail(user)">
                        mdi-alert
                      </v-icon>
                    </td>
                    <td class="text-body-2">{{ user.email }}</td>
                    <td>
                      <v-chip x-small
                        :color="user.status === 'active' ? '#10B981' : user.status === 'blocked' ? '#EF4444' : '#F59E0B'"
                        text-color="white">
                        {{ user.status }}
                      </v-chip>
                      <v-chip v-if="user.isAllowlisted" x-small color="#007AFF" text-color="white" class="ml-1">
                        allowlisted
                      </v-chip>
                    </td>
                    <td>
                      <v-chip x-small v-if="user.isSuperAdmin" color="#8b5cf6" text-color="white">Super Admin</v-chip>
                      <v-chip x-small v-else-if="user.isAdmin" color="#007AFF" text-color="white">Admin</v-chip>
                      <span v-else class="text-caption grey--text">User</span>
                    </td>
                    <td class="text-body-2 text-capitalize">{{ user.loginProvider || '—' }}</td>
                    <td class="text-caption grey--text">{{ formatAuditDate(user.createdAt) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else-if="!userDirectoryLoading" class="text-center py-3 grey--text text-caption">
                No users found.
              </div>
              <div v-if="filteredUserDirectory.length > userDirectoryPageSize" class="d-flex justify-center mt-3">
                <v-pagination
                  v-model="userDirectoryPage"
                  :length="Math.ceil(filteredUserDirectory.length / userDirectoryPageSize)"
                  :total-visible="7"
                  color="#007AFF"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-dialog v-model="conflictDialog" max-width="500">
          <v-card>
            <v-card-title class="text-subtitle-1">
              <v-icon left color="warning">mdi-alert</v-icon>
              Access Conflict Detected
            </v-card-title>
            <v-card-text>
              <div class="text-body-2 mb-2">
                <strong>{{ conflictDialogUser.name || conflictDialogUser.email }}</strong>
                ({{ conflictDialogUser.email }})
              </div>
              <v-alert v-for="(warning, i) in conflictDialogWarnings" :key="i" type="warning" dense outlined class="mb-2">
                {{ warning }}
              </v-alert>
              <div class="text-body-2 mt-3">
                <strong>Precedence order:</strong>
                <ol class="mt-1">
                  <li>Blocklist (highest priority — blocks login)</li>
                  <li>Deactivation (blocks login, data preserved)</li>
                  <li>Allowlist (allows login if no block/deactivation)</li>
                </ol>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="conflictDialog = false">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="conflictWarningDialog" max-width="500" persistent>
          <v-card>
            <v-card-title class="text-subtitle-1">
              <v-icon left color="warning">mdi-alert</v-icon>
              Conflict Warning
            </v-card-title>
            <v-card-text>
              <v-alert v-for="(warning, i) in pendingConflictWarnings" :key="i" type="warning" dense outlined class="mb-2">
                {{ warning }}
              </v-alert>
              <div class="text-body-2 mt-2">Do you want to proceed anyway?</div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text @click="cancelConflictAction">Cancel</v-btn>
              <v-btn color="warning" dark @click="confirmConflictAction">Proceed</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-tab-item>

      <v-tab-item>
        <v-alert v-if="configHasError" type="error" dismissible class="mt-4 mb-4">
          {{ configErrorMessage }}
        </v-alert>
        <v-progress-linear v-if="configIsLoading" indeterminate color="#007AFF" class="mt-4 mb-4" />
        <v-alert v-if="configSaveSuccess" type="success" dismissible class="mt-4 mb-4">
          Company profile saved successfully
        </v-alert>

        <v-row class="mt-4">
          <v-col cols="12" md="8">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-4 theme-heading">
                Company Profile
              </h3>

              <v-text-field
                v-model="companyConfig.companyName"
                label="Company Name"
                outlined
                dense
                color="#007AFF"
                class="mb-3"
              />

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="companyConfig.industry"
                    :items="industryOptions"
                    label="Industry"
                    outlined
                    dense
                    color="#007AFF"
                    class="mb-3"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="companyConfig.size"
                    :items="sizeOptions"
                    label="Company Size"
                    outlined
                    dense
                    color="#007AFF"
                    class="mb-3"
                  />
                </v-col>
              </v-row>

              <v-text-field
                v-model="companyConfig.website"
                label="Website URL"
                outlined
                dense
                color="#007AFF"
                class="mb-3"
                prepend-inner-icon="mdi-web"
              />

              <v-text-field
                v-model="companyConfig.wikipediaUrl"
                label="Wikipedia URL (optional)"
                outlined
                dense
                color="#007AFF"
                class="mb-3"
                prepend-inner-icon="mdi-wikipedia"
              />

              <v-textarea
                v-model="companyConfig.description"
                label="Company Description"
                outlined
                dense
                rows="4"
                color="#007AFF"
                class="mb-3"
              />

              <v-select
                v-model="companyConfig.region"
                :items="regionOptions"
                label="Region"
                outlined
                dense
                color="#007AFF"
                class="mb-3"
              />
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Succession Bench Ratio
              </h3>
              <div class="text-caption grey--text text--darken-1 mb-3">
                Target bench-to-seat ratio for succession planning
              </div>

              <v-select
                v-model="companyConfig.successionBenchRatio"
                :items="benchRatioOptions"
                label="Bench Ratio"
                outlined
                dense
                color="#007AFF"
                class="mb-3"
              />

              <div class="text-caption grey--text mb-3">Industry Presets:</div>
              <v-chip
                v-for="preset in industryPresets"
                :key="preset.label"
                small
                outlined
                :color="companyConfig.successionBenchRatio === preset.ratio ? '#007AFF' : '#9E9E9E'"
                class="mr-2 mb-2"
                @click="companyConfig.successionBenchRatio = preset.ratio"
              >
                {{ preset.label }}: {{ preset.ratio }}
              </v-chip>
            </v-card>

            <v-btn
              block
              color="#007AFF"
              dark
              large
              :loading="configIsSaving"
              @click="saveConfig"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save Configuration
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mt-2" v-if="isSuperAdmin">
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                <v-icon left class="theme-heading">mdi-robot-happy</v-icon>
                Sage HR Mode
              </h3>
              <div class="text-caption grey--text text--darken-1 mb-4">
                Controls how Sage HR processes talent queries across your organization. This setting applies to all users.
              </div>

              <v-row dense align="center">
                <v-col cols="auto">
                  <v-switch
                    :input-value="sageHRMode === 'react'"
                    @change="handleSageHRModeToggle"
                    :loading="sageHRModeLoading"
                    :disabled="sageHRModeLoading"
                    color="#8b5cf6"
                    dense
                    hide-details
                  >
                    <template v-slot:label>
                      <span class="font-weight-bold" :style="{ color: sageHRMode === 'react' ? '#8b5cf6' : '' }">
                        {{ sageHRMode === 'react' ? 'Sage HR+ (ReAct Agent)' : 'Standard Mode' }}
                      </span>
                    </template>
                  </v-switch>
                </v-col>
              </v-row>

              <v-row dense class="mt-3">
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3" :style="sageHRMode === 'react' ? 'border-color: #8b5cf6' : ''">
                    <div class="d-flex align-center mb-2">
                      <v-icon :color="sageHRMode === 'react' ? '#8b5cf6' : 'grey'" class="mr-2">mdi-brain</v-icon>
                      <span class="font-weight-bold text-body-2">HR+ (ReAct Agent)</span>
                      <v-chip v-if="sageHRMode === 'react'" x-small color="#8b5cf6" dark class="ml-2">Active</v-chip>
                    </div>
                    <div class="text-caption grey--text text--darken-1">
                      Enhanced multi-step reasoning. Sage HR uses tool-calling to perform complex, multi-step talent queries — searching profiles, analyzing gaps, building teams, and synthesizing insights in a single conversation turn.
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-3" :style="sageHRMode === 'standard' ? 'border-color: #007AFF' : ''">
                    <div class="d-flex align-center mb-2">
                      <v-icon :color="sageHRMode === 'standard' ? '#007AFF' : 'grey'" class="mr-2">mdi-chat-processing</v-icon>
                      <span class="font-weight-bold text-body-2">Standard</span>
                      <v-chip v-if="sageHRMode === 'standard'" x-small color="#007AFF" dark class="ml-2">Active</v-chip>
                    </div>
                    <div class="text-caption grey--text text--darken-1">
                      Single-step conversational mode. Sage HR responds directly to queries without multi-tool reasoning. Faster responses, best for simple talent questions.
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
                Facet Scoring Tiers
              </h3>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                Evidence quality thresholds used for deterministic scoring.
              </div>
              <v-row dense>
                <v-col v-for="tier in scoringTiers" :key="tier.label" cols="6" md="3">
                  <v-card
                    flat
                    class="text-center pa-4"
                    :style="{ background: tier.bg, border: '1px solid ' + tier.color + '30', borderRadius: '10px' }"
                  >
                    <div class="text-h5 font-weight-black" :style="{ color: tier.color }">
                      {{ tier.label }}
                    </div>
                    <div class="text-body-2 grey--text">{{ tier.range }}</div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
                Handler Category Weights
              </h3>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                Adjust org-wide default weights for each scoring category.
              </div>

              <v-progress-linear v-if="scoringWeightsLoading" indeterminate color="#007AFF" class="mb-4" />

              <div
                v-for="cat in handlerCategories"
                :key="cat.name"
                class="d-flex align-center py-3"
                style="border-bottom: 1px solid var(--resu-border, #e0e0e0)"
              >
                <v-icon class="mr-3" color="#007AFF">{{ cat.icon }}</v-icon>
                <div class="flex-grow-1" style="min-width: 180px">
                  <div class="text-body-2 font-weight-bold">{{ cat.name }}</div>
                </div>
                <v-slider
                  :value="scoringWeights[cat.name]"
                  @input="updateWeight(cat.name, $event)"
                  min="0"
                  max="100"
                  hide-details
                  color="#007AFF"
                  style="max-width: 200px"
                  class="mx-4"
                />
                <div class="text-body-2 font-weight-bold" style="color: #007AFF; min-width: 40px; text-align: right">
                  {{ scoringWeights[cat.name] }}
                </div>
              </div>

              <div class="d-flex align-center mt-5" style="gap: 12px">
                <v-btn
                  color="#007AFF"
                  dark
                  :loading="scoringWeightsSaving"
                  @click="handleSaveWeights"
                >
                  Save Defaults
                </v-btn>
                <v-btn
                  outlined
                  @click="handleResetWeights"
                >
                  Reset
                </v-btn>
              </div>

              <div class="mt-4 mb-2">
                <div class="text-caption grey--text text--darken-1 mb-2">Preset Profiles:</div>
                <div class="d-flex flex-wrap" style="gap: 8px">
                  <v-chip
                    v-for="preset in weightPresets"
                    :key="preset.label"
                    small
                    outlined
                    color="#007AFF"
                    @click="applyPreset(preset)"
                  >
                    {{ preset.label }}
                  </v-chip>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-alert color="blue lighten-5" border="left" colored-border>
              <strong style="color: #007AFF">Compliance Notice:</strong>
              All scoring is deterministic and auditable. Changes to weights affect how results are ordered but not the underlying evidence assessment.
            </v-alert>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
                Hierarchy Overview
              </h3>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                AI-inferred organizational structure from {{ hierarchyData ? (hierarchyData.total_profiles || 0).toLocaleString() : '...' }} profiles
              </div>

              <v-progress-linear v-if="hierarchyLoading" indeterminate color="#007AFF" class="mb-4" />

              <div v-if="!hierarchyLoading && hierarchyData && hierarchyData.level_counts" class="d-flex flex-wrap mb-4" style="gap: 8px">
                <v-chip
                  v-for="level in hierarchyOrder"
                  :key="level"
                  small
                  :color="seniorityColors[level]"
                  text-color="white"
                  class="font-weight-bold"
                >
                  {{ level }}: {{ hierarchyData.level_counts[level] || 0 }}
                </v-chip>
              </div>

              <div v-if="!hierarchyLoading && hierarchyData && hierarchyData.departments">
                <div class="text-overline grey--text mb-2">
                  Departments ({{ hierarchyData.departments.length }})
                </div>
                <div class="d-flex flex-wrap" style="gap: 6px">
                  <v-chip
                    v-for="dept in hierarchyData.departments"
                    :key="typeof dept === 'string' ? dept : dept.name"
                    small
                    outlined
                    color="primary"
                  >
                    {{ typeof dept === 'string' ? dept : dept.name }}
                    <span v-if="typeof dept === 'object' && dept.count" class="ml-1 text--secondary">({{ dept.count }})</span>
                  </v-chip>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
                Hierarchy Assistant
              </h3>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                Describe your organization's structure and the AI will parse it into title-to-level mappings.
              </div>
              <v-textarea
                v-model="orgDescription"
                placeholder="Example: We have a CEO, two VPs (Engineering and Product), three Directors under each VP, and about 20 engineers split across frontend, backend, and data teams..."
                outlined
                dense
                rows="4"
                color="#007AFF"
                class="mb-2"
              />
              <v-btn
                color="#007AFF"
                dark
                :loading="hierarchyAssistantLoading"
                :disabled="!orgDescription.trim()"
                @click="handleApplyAssistant"
              >
                Apply Structure
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
                Level Reassignment
              </h3>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                Customize which titles map to each seniority level.
              </div>

              <v-row dense class="mb-4">
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model="overrideInput"
                    placeholder="Add title keyword (e.g. 'staff engineer')"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="overrideLevel"
                    :items="hierarchyOrder"
                    placeholder="Assign to level..."
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-btn
                    color="#007AFF"
                    dark
                    block
                    :disabled="!overrideInput.trim() || !overrideLevel"
                    @click="handleAddOverride"
                  >
                    Add Override
                  </v-btn>
                </v-col>
              </v-row>

              <v-alert
                v-if="levelOverrides && Object.keys(levelOverrides).length > 0"
                color="orange lighten-5"
                border="left"
                colored-border
                class="mb-4"
              >
                <div class="text-caption font-weight-bold mb-2" style="color: #92400e">
                  Active Overrides ({{ Object.keys(levelOverrides).length }})
                </div>
                <div class="d-flex flex-wrap" style="gap: 6px">
                  <v-chip
                    v-for="(level, pattern) in levelOverrides"
                    :key="pattern"
                    small
                    close
                    :color="seniorityColors[level]"
                    text-color="white"
                    @click:close="handleDeleteOverride(pattern)"
                  >
                    "{{ pattern }}" &rarr; {{ level }}
                  </v-chip>
                </div>
              </v-alert>

              <v-expansion-panels flat accordion>
                <v-expansion-panel
                  v-for="level in hierarchyOrder"
                  :key="level"
                >
                  <v-expansion-panel-header>
                    <div class="d-flex align-center" style="gap: 10px">
                      <span class="level-dot" :style="{ backgroundColor: seniorityColors[level] }"></span>
                      <span class="font-weight-bold">{{ level }}</span>
                      <span class="text-caption grey--text">
                        {{ getLevelTitles(level).length }} unique titles, {{ getLevelTotalCount(level) }} people
                      </span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="getLevelTitles(level).length === 0" class="text-body-2 grey--text font-italic pa-2">
                      No titles mapped to this level
                    </div>
                    <div v-else>
                      <div
                        v-for="(t, i) in getLevelTitles(level).slice(0, 30)"
                        :key="i"
                        class="d-flex align-center justify-space-between pa-2 mb-1"
                        style="border: 1px solid var(--resu-border, #e0e0e0); border-radius: 6px"
                      >
                        <div class="d-flex align-center" style="gap: 8px">
                          <span class="text-body-2">{{ t.title }}</span>
                          <v-chip x-small outlined>{{ t.count }}</v-chip>
                        </div>
                        <v-select
                          :items="hierarchyOrder.filter(l => l !== level)"
                          placeholder="Move to..."
                          dense
                          outlined
                          hide-details
                          style="max-width: 160px"
                          @change="handleReassign(t.title, $event)"
                        />
                      </div>
                      <div v-if="getLevelTitles(level).length > 30" class="text-caption grey--text font-italic pa-2">
                        +{{ getLevelTitles(level).length - 30 }} more titles
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
                AI Provider Configuration
              </h3>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                Assign an AI provider and model to each feature. Changes apply globally for your company.
              </div>

              <v-progress-linear v-if="aiConfigLoading" indeterminate color="#007AFF" class="mb-4" />
              <v-alert v-if="aiConfigSaveSuccess" type="success" dismissible class="mb-4">
                AI provider configuration saved successfully
              </v-alert>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                Available Providers
              </h3>
              <div class="d-flex flex-wrap" style="gap: 12px">
                <v-card
                  v-for="provider in allAIProviders"
                  :key="provider.id"
                  flat
                  class="pa-3"
                  style="border-radius: 10px; min-width: 200px"
                  :style="{ border: '2px solid ' + provider.color + '40' }"
                >
                  <div class="d-flex align-center mb-2" style="gap: 8px">
                    <v-icon :color="provider.color">{{ provider.icon }}</v-icon>
                    <span class="font-weight-bold text-body-2">{{ provider.name }}</span>
                    <v-chip
                      v-if="!provider.isCustom"
                      x-small
                      outlined
                      color="#4ade80"
                      style="font-size: 10px"
                    >built-in</v-chip>
                    <v-chip
                      v-else
                      x-small
                      outlined
                      color="#f59e0b"
                      style="font-size: 10px"
                    >custom</v-chip>
                  </div>
                  <div class="text-caption grey--text mb-2">
                    {{ provider.models.length }} models available
                  </div>
                  <div class="d-flex align-center" style="gap: 6px">
                    <v-chip
                      v-if="provider.supportsToolCalling"
                      x-small
                      outlined
                      color="green"
                    >Tool Calling</v-chip>
                    <v-btn
                      x-small
                      text
                      color="#007AFF"
                      @click="handleTestProvider(provider.id)"
                      :loading="aiTestResults[provider.id] && aiTestResults[provider.id].status === 'testing'"
                    >Test</v-btn>
                    <v-icon
                      v-if="aiTestResults[provider.id] && aiTestResults[provider.id].status === 'success'"
                      small
                      color="green"
                    >mdi-check-circle</v-icon>
                    <v-icon
                      v-if="aiTestResults[provider.id] && aiTestResults[provider.id].status === 'error'"
                      small
                      color="red"
                    >mdi-alert-circle</v-icon>
                  </div>
                </v-card>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 font-weight-bold theme-heading">
                  Feature Assignments
                </h3>
                <v-btn
                  x-small
                  outlined
                  color="#F59E0B"
                  @click="handleResetAIDefaults"
                >
                  <v-icon x-small left>mdi-restore</v-icon>
                  Reset to Defaults
                </v-btn>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                Select which provider and model each AI feature should use. Click the info icon next to any model to see cost, speed, and quality details.
              </div>

              <div
                v-for="feature in aiFeatures"
                :key="feature.key"
                class="py-3"
                style="border-bottom: 1px solid var(--resu-border, #e0e0e0)"
              >
                <div class="d-flex align-center">
                  <v-icon class="mr-3" color="#007AFF">{{ feature.icon }}</v-icon>
                  <div style="min-width: 220px" class="mr-4">
                    <div class="text-body-2 font-weight-bold">{{ feature.label }}</div>
                    <div class="text-caption grey--text">{{ feature.description }}</div>
                  </div>
                  <v-select
                    :value="getFeatureAssignment(feature.key).providerId"
                    @input="handleProviderChange(feature.key, $event)"
                    :items="aiProviderOptions"
                    label="Provider"
                    outlined
                    dense
                    hide-details
                    color="#007AFF"
                    style="max-width: 200px"
                    class="mr-3"
                  />
                  <v-menu
                    :close-on-content-click="false"
                    offset-y
                    bottom
                    max-width="360"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div class="d-flex align-center">
                        <v-select
                          :value="getFeatureAssignment(feature.key).model"
                          @input="handleModelChange(feature.key, $event)"
                          :items="getModelDropdownItems(feature.key)"
                          label="Model"
                          outlined
                          dense
                          hide-details
                          color="#007AFF"
                          style="max-width: 220px"
                        >
                          <template v-slot:item="{ item, on: itemOn, attrs: itemAttrs }">
                            <v-list-item v-bind="itemAttrs" v-on="itemOn">
                              <v-list-item-content>
                                <v-list-item-title>{{ item.text }}</v-list-item-title>
                                <v-list-item-subtitle v-if="item.info" class="text-caption">
                                  <span :style="{ color: item.info.costColor }">{{ item.info.cost }}</span>
                                  <span class="mx-1">|</span>
                                  <span>{{ item.info.speed }}</span>
                                  <span class="mx-1">|</span>
                                  <span>{{ item.info.quality }}</span>
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-select>
                        <v-btn
                          icon
                          x-small
                          class="ml-1"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon small color="grey">mdi-information-outline</v-icon>
                        </v-btn>
                      </div>
                    </template>
                    <v-card class="pa-3" style="border-radius: 10px">
                      <div
                        v-for="m in getModelsForFeature(feature.key)"
                        :key="m"
                        class="mb-3 pa-2"
                        :class="{ 'model-info-selected': getFeatureAssignment(feature.key).model === m }"
                        style="border-radius: 8px; cursor: pointer; border: 1px solid var(--resu-border, #e0e0e0)"
                        @click="handleModelChange(feature.key, m)"
                      >
                        <div class="d-flex align-center justify-space-between mb-1">
                          <span class="text-body-2 font-weight-bold">{{ m }}</span>
                          <v-icon
                            v-if="getFeatureAssignment(feature.key).model === m"
                            small
                            color="#007AFF"
                          >mdi-check-circle</v-icon>
                        </div>
                        <div v-if="getModelMeta(m)" class="d-flex flex-wrap align-center mb-1" style="gap: 6px">
                          <v-chip
                            x-small
                            :color="getModelMeta(m).costColor"
                            text-color="white"
                            class="font-weight-bold"
                          >{{ getModelMeta(m).cost }}</v-chip>
                          <v-chip x-small outlined>
                            <v-icon x-small left>{{ getModelMeta(m).speedIcon }}</v-icon>
                            {{ getModelMeta(m).speed }}
                          </v-chip>
                          <v-chip x-small outlined color="#007AFF">
                            {{ getModelMeta(m).quality }}
                          </v-chip>
                          <v-chip x-small outlined>
                            {{ getModelMeta(m).contextWindow }}
                          </v-chip>
                        </div>
                        <div v-if="getModelMeta(m)" class="text-caption grey--text text--darken-1">
                          {{ getModelMeta(m).bestFor }}
                        </div>
                      </div>
                    </v-card>
                  </v-menu>
                </div>
              </div>

              <div class="d-flex align-center mt-5" style="gap: 12px">
                <v-btn
                  color="#007AFF"
                  dark
                  :loading="aiConfigSaving"
                  @click="handleSaveAIConfig"
                >
                  Save Configuration
                </v-btn>
                <v-btn outlined @click="handleSetAllProvider('openai')">
                  All OpenAI
                </v-btn>
                <v-btn outlined @click="handleSetAllProvider('anthropic')">
                  All Claude
                </v-btn>
                <v-btn outlined @click="handleSetAllProvider('google')">
                  All Gemini
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-if="isSuperAdmin">
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2 theme-heading">
                Add Custom Provider
              </h3>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                Add your own AI provider for use across features. Must support the OpenAI-compatible chat completions API format.
              </div>
              <v-row dense>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="newProvider.name"
                    label="Provider Name"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="newProvider.endpoint"
                    label="API Endpoint URL"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                    placeholder="https://api.example.com/v1"
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="newProvider.models"
                    label="Model Names (comma-separated)"
                    outlined
                    dense
                    color="#007AFF"
                    hide-details
                    placeholder="model-a, model-b"
                  />
                </v-col>
                <v-col cols="12" sm="3">
                  <v-btn
                    color="#007AFF"
                    dark
                    block
                    :disabled="!newProvider.name.trim() || !newProvider.endpoint.trim() || !newProvider.models.trim()"
                    @click="handleAddCustomProvider"
                  >
                    Add Provider
                  </v-btn>
                </v-col>
              </v-row>

              <div v-if="customProviders.length > 0" class="mt-4">
                <div class="text-caption font-weight-bold mb-2 theme-heading">
                  Custom Providers ({{ customProviders.length }})
                </div>
                <div class="d-flex flex-wrap" style="gap: 8px">
                  <v-chip
                    v-for="cp in customProviders"
                    :key="cp.id"
                    small
                    close
                    color="orange lighten-4"
                    @click:close="handleRemoveCustomProvider(cp.id)"
                  >
                    {{ cp.name }} ({{ cp.models.length }} models)
                  </v-chip>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4 mb-4">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 font-weight-bold theme-heading">
                  Configuration Change Log
                </h3>
                <v-btn
                  x-small
                  text
                  color="#007AFF"
                  @click="loadChangeLog"
                  :loading="changeLogLoading"
                >
                  <v-icon x-small left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-3">
                All configuration changes are recorded for audit compliance.
              </div>

              <v-progress-linear v-if="changeLogLoading" indeterminate color="#007AFF" class="mb-3" />

              <div v-if="!changeLogLoading && aiChangeLog.length === 0" class="text-body-2 grey--text font-italic pa-2">
                No configuration changes recorded yet.
              </div>

              <div
                v-for="(entry, idx) in aiChangeLog.slice(0, 20)"
                :key="idx"
                class="d-flex align-start pa-2 mb-1"
                style="border: 1px solid var(--resu-border, #e0e0e0); border-radius: 6px"
              >
                <v-icon small class="mr-2 mt-1" :color="getChangeIcon(entry.changeType).color">
                  {{ getChangeIcon(entry.changeType).icon }}
                </v-icon>
                <div class="flex-grow-1">
                  <div class="d-flex align-center justify-space-between">
                    <span class="text-body-2 font-weight-bold">{{ entry.description }}</span>
                    <span class="text-caption grey--text">{{ formatChangeDate(entry.timestamp) }}</span>
                  </div>
                  <div class="text-caption grey--text">
                    {{ entry.changedBy || 'Admin' }}
                    <span v-if="entry.previousValue"> | Was: {{ entry.previousValue }}</span>
                    <span v-if="entry.newValue"> | Now: {{ entry.newValue }}</span>
                  </div>
                </div>
              </div>

              <div v-if="aiChangeLog.length > 20" class="text-caption grey--text font-italic pa-2 text-center">
                Showing 20 of {{ aiChangeLog.length }} entries
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-alert color="blue lighten-5" border="left" colored-border>
              <strong style="color: #007AFF">How This Works:</strong>
              Provider assignments are stored in your company configuration. When a feature makes an AI call, the backend reads the assigned provider and model from config, then routes the request to that provider's API. All providers must have valid API keys configured in your backend environment settings. All changes are logged for compliance auditing.
            </v-alert>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12" sm="6">
            <v-card flat class="simpleCard pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon color="#2b7fff" class="mr-2">mdi-package-variant-closed</v-icon>
                <div>
                  <div class="text-overline" style="line-height: 1.2">Total Employees</div>
                  <div class="text-h5 font-weight-bold" style="color: #2b7fff">{{ dataExportTotalEmployees }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card flat class="simpleCard pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon color="#00c950" class="mr-2">mdi-clock-outline</v-icon>
                <div>
                  <div class="text-overline" style="line-height: 1.2">Last Synced</div>
                  <div class="text-h5 font-weight-bold" style="color: #00c950">{{ lastSyncedDisplay }}</div>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <v-row class="pt-4">
          <v-col cols="12" md="8" offset-md="2">
            <v-card flat class="simpleCard">
              <v-card-text class="d-flex flex-column align-center">
                <div>
                  <v-icon x-large class="icon-circle">
                    mdi-package-variant-closed
                  </v-icon>
                </div>
                <div class="text-h6 font-weight-bold text--primary pt-5">
                  Export All Employee Profiles
                </div>
                <div class="text-body-2 pt-3">
                  Download all employee profile data as a single export file.
                </div>
                <v-btn
                  class="mt-5"
                  color="#007AFF"
                  dark
                  @click="handleExportProfiles"
                  :loading="dataExporting"
                >
                  <v-icon small class="mr-1">mdi-tray-arrow-down</v-icon>
                  Export {{ dataExportTotalEmployees }} Profiles
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12">
            <v-card flat class="simpleCard pa-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                <v-icon left class="theme-heading">mdi-cached</v-icon>
                Profile Cache Management
              </h3>
              <div class="text-caption grey--text text--darken-1 mb-4">
                The profile cache stores all employee profiles in memory for fast search and analytics. Use manual refresh to pick up newly added profiles from Cosmos DB.
              </div>

              <v-row dense>
                <v-col cols="12" md="4">
                  <v-card outlined class="pa-3 text-center">
                    <div class="text-h4 font-weight-bold" style="color: #007AFF">
                      {{ cacheStatus.profileCount || '—' }}
                    </div>
                    <div class="text-caption grey--text">Profiles Cached</div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card outlined class="pa-3 text-center">
                    <div class="text-h6 font-weight-bold" :style="{ color: cacheAgeColor }">
                      {{ cacheAgeDisplay }}
                    </div>
                    <div class="text-caption grey--text">Cache Age</div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card outlined class="pa-3 text-center">
                    <v-icon :color="cacheStatus.isCacheLoaded ? 'success' : 'grey'">
                      {{ cacheStatus.isCacheLoaded ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <div class="text-caption grey--text mt-1">{{ cacheStatus.isCacheLoaded ? 'Active' : 'Not Loaded' }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <v-row dense align="center">
                <v-col cols="12" md="6">
                  <v-btn
                    color="#007AFF"
                    dark
                    :loading="cacheRefreshing"
                    :disabled="cacheRefreshing"
                    @click="refreshCache"
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh Profile Cache
                  </v-btn>
                  <span v-if="cacheRefreshResult" class="ml-3 text-body-2 success--text">
                    {{ cacheRefreshResult }}
                  </span>
                </v-col>
                <v-col cols="12" md="6">
                  <v-progress-linear
                    v-if="cacheRefreshing"
                    indeterminate
                    color="#007AFF"
                    class="mt-2"
                    height="6"
                    rounded
                  />
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <h4 class="text-subtitle-2 font-weight-bold mb-3 theme-heading">
                Scheduled Refresh
              </h4>
              <v-row dense align="center">
                <v-col cols="auto">
                  <v-switch
                    v-model="companyConfig.nightlyRefreshEnabled"
                    label="Enable nightly refresh"
                    color="#007AFF"
                    dense
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="companyConfig.nightlyRefreshTime"
                    label="Refresh Time (24h)"
                    outlined
                    dense
                    color="#007AFF"
                    :disabled="!companyConfig.nightlyRefreshEnabled"
                    placeholder="02:00"
                    prepend-inner-icon="mdi-clock-outline"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="companyConfig.nightlyRefreshTimezone"
                    :items="timezoneOptions"
                    label="Timezone"
                    outlined
                    dense
                    color="#007AFF"
                    :disabled="!companyConfig.nightlyRefreshEnabled"
                    prepend-inner-icon="mdi-earth"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption grey--text">
                    When enabled, the profile cache automatically refreshes at the specified time daily. Currently {{ companyConfig.nightlyRefreshEnabled ? 'ON' : 'OFF' }}.
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row class="mt-4">
          <v-col cols="12" md="8" offset-md="2">
            <h2 class="text-h5 font-weight-bold mb-1 theme-heading">
              Company Branding
            </h2>
            <p class="text-subtitle-2 grey--text text--darken-1 mb-6">
              Customize the look and feel of the platform for your organization
            </p>

            <v-card outlined class="pa-5 mb-6">
              <div class="text-subtitle-1 font-weight-bold mb-4 theme-heading">
                <v-icon small color="primary" class="mr-1">mdi-domain</v-icon>
                Company Identity
              </div>
              <v-text-field
                v-model="brandingForm.companyName"
                label="Company Name"
                hint="Replaces 'Resumation Nexus' throughout the app"
                persistent-hint
                outlined
                dense
                class="mb-3"
              />
              <v-text-field
                v-model="brandingForm.loginTagline"
                label="Login Page Tagline"
                hint="Custom welcome text on the login page (e.g., 'Welcome to Acme Talent Hub')"
                persistent-hint
                outlined
                dense
              />
            </v-card>

            <v-card outlined class="pa-5 mb-6">
              <div class="text-subtitle-1 font-weight-bold mb-4 theme-heading">
                <v-icon small color="primary" class="mr-1">mdi-palette</v-icon>
                Brand Colors
              </div>
              <v-row>
                <v-col cols="12" sm="4">
                  <div class="text-caption font-weight-bold mb-1">Primary Color</div>
                  <div class="d-flex align-center">
                    <input type="color" v-model="brandingForm.primaryColor" style="width: 40px; height: 40px; border: none; cursor: pointer;" />
                    <v-text-field v-model="brandingForm.primaryColor" dense outlined hide-details class="ml-2" style="max-width: 120px;" />
                  </div>
                  <div class="text-caption grey--text mt-1">Buttons, links, accents</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="text-caption font-weight-bold mb-1">Header Color</div>
                  <div class="d-flex align-center">
                    <input type="color" v-model="brandingForm.headerColor" style="width: 40px; height: 40px; border: none; cursor: pointer;" />
                    <v-text-field v-model="brandingForm.headerColor" dense outlined hide-details class="ml-2" style="max-width: 120px;" />
                  </div>
                  <div class="text-caption grey--text mt-1">App bar background</div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="text-caption font-weight-bold mb-1">Accent Color</div>
                  <div class="d-flex align-center">
                    <input type="color" v-model="brandingForm.accentColor" style="width: 40px; height: 40px; border: none; cursor: pointer;" />
                    <v-text-field v-model="brandingForm.accentColor" dense outlined hide-details class="ml-2" style="max-width: 120px;" />
                  </div>
                  <div class="text-caption grey--text mt-1">Secondary highlights</div>
                </v-col>
              </v-row>
            </v-card>

            <v-card outlined class="pa-5 mb-6">
              <div class="text-subtitle-1 font-weight-bold mb-4 theme-heading">
                <v-icon small color="primary" class="mr-1">mdi-image</v-icon>
                Company Logo
              </div>
              <div class="d-flex align-center mb-3">
                <v-avatar size="64" class="mr-4" style="border: 2px solid #eee;">
                  <img v-if="brandingForm.logoBase64" :src="brandingForm.logoBase64" alt="Logo preview" />
                  <v-icon v-else size="32" color="grey lighten-1">mdi-image-plus</v-icon>
                </v-avatar>
                <div>
                  <v-btn small outlined color="primary" @click="$refs.logoUpload.click()">
                    <v-icon small left>mdi-upload</v-icon>
                    Upload Logo
                  </v-btn>
                  <v-btn v-if="brandingForm.logoBase64" small text color="error" class="ml-2" @click="brandingForm.logoBase64 = ''">
                    Remove
                  </v-btn>
                  <div class="text-caption grey--text mt-1">Recommended: square image, at least 128x128px. PNG or JPG.</div>
                </div>
              </div>
              <input ref="logoUpload" type="file" accept="image/png,image/jpeg,image/svg+xml" style="display: none;" @change="handleLogoUpload" />
            </v-card>

            <v-card outlined class="pa-5 mb-6">
              <div class="text-subtitle-1 font-weight-bold mb-4 theme-heading">
                <v-icon small color="primary" class="mr-1">mdi-star-circle</v-icon>
                Favicon
              </div>
              <div class="d-flex align-center mb-3">
                <v-avatar size="32" tile class="mr-4" style="border: 1px solid #eee;">
                  <img v-if="brandingForm.faviconBase64" :src="brandingForm.faviconBase64" alt="Favicon preview" />
                  <v-icon v-else size="20" color="grey lighten-1">mdi-star-circle</v-icon>
                </v-avatar>
                <div>
                  <v-btn small outlined color="primary" @click="$refs.faviconUpload.click()">
                    <v-icon small left>mdi-upload</v-icon>
                    Upload Favicon
                  </v-btn>
                  <v-btn v-if="brandingForm.faviconBase64" small text color="error" class="ml-2" @click="brandingForm.faviconBase64 = ''">
                    Remove
                  </v-btn>
                  <div class="text-caption grey--text mt-1">Recommended: 32x32 or 64x64 PNG.</div>
                </div>
              </div>
              <input ref="faviconUpload" type="file" accept="image/png,image/x-icon,image/svg+xml" style="display: none;" @change="handleFaviconUpload" />
            </v-card>

            <v-card outlined class="pa-5 mb-6">
              <div class="text-subtitle-1 font-weight-bold mb-4 theme-heading">
                <v-icon small color="primary" class="mr-1">mdi-information</v-icon>
                Attribution
              </div>
              <v-switch
                v-model="brandingForm.poweredByVisible"
                label="Show 'Powered by Resumation' badge"
                hint="Displays a small Resumation attribution on the login page and app header"
                persistent-hint
                color="primary"
              />
            </v-card>

            <v-card v-if="brandingForm.companyName" outlined class="pa-5 mb-6" style="border: 2px dashed #007AFF;">
              <div class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
                <v-icon small color="primary" class="mr-1">mdi-eye</v-icon>
                Preview
              </div>
              <div class="d-flex align-center pa-3 rounded" :style="{ background: brandingForm.headerColor || '#1E3A5F' }">
                <v-avatar size="30" style="background: white;">
                  <img v-if="brandingForm.logoBase64" :src="brandingForm.logoBase64" alt="" style="object-fit: cover;" />
                  <v-icon v-else size="20" color="primary">mdi-domain</v-icon>
                </v-avatar>
                <span class="white--text text-subtitle-1 font-weight-bold ml-2">{{ brandingForm.companyName }}</span>
                <v-spacer />
                <span v-if="brandingForm.poweredByVisible" class="white--text caption" style="opacity: 0.6;">Powered by Resumation</span>
              </div>
            </v-card>

            <div class="d-flex justify-end">
              <v-btn text class="mr-2" @click="resetBrandingForm">
                Reset
              </v-btn>
              <v-btn color="primary" :loading="brandingSaving" @click="saveBranding">
                <v-icon left small>mdi-content-save</v-icon>
                Save Branding
              </v-btn>
            </div>

            <v-alert v-if="brandingSaveSuccess" type="success" dense outlined class="mt-4">
              Branding configuration saved successfully. Changes have been applied.
            </v-alert>
            <v-alert v-if="brandingSaveError" type="error" dense outlined class="mt-4">
              {{ brandingSaveError }}
            </v-alert>
          </v-col>
        </v-row>
      </v-tab-item>

    </v-tabs-items>

    <v-snackbar v-model="showToast" :timeout="3000" color="success" bottom right>
      {{ toastMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";
import * as companyApi from "../../api/CompanyApi";
import * as adminApi from "../../api/IntelligenceAdminApi";
import * as authApi from "../../api/AuthApi";
import * as brandingApi from "../../api/BrandingApi";
import { exportAllEmployeeProfiles } from "../../utility/profileExport";
import * as themeUtil from "../../utility/theme";

const HIERARCHY_ORDER = ["C-Suite", "VP", "Director", "Manager", "Senior", "Mid-Level", "Junior"];

const SENIORITY_COLORS = {
  "C-Suite": "#8b5cf6",
  "VP": "#6366f1",
  "Director": "#3b82f6",
  "Manager": "#14b8a6",
  "Senior": "#22c55e",
  "Mid-Level": "#9ca3af",
  "Junior": "#f59e0b",
};

const SCORING_TIERS = [
  { label: "Elite", range: "85-95%", color: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
  { label: "Proven", range: "50-75%", color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  { label: "Mention", range: "25-45%", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  { label: "Weak", range: "10-20%", color: "#ef4444", bg: "rgba(239,68,68,0.12)" },
];

const WEIGHT_PRESETS = [
  {
    label: "Balanced",
    weights: { "Leadership & Team Size": 50, "Financial & Impact Metrics": 50, "Experience & Tenure": 50, "Skills & Tools": 50, "Domain & Industry": 50, "Compliance & Certification": 50 },
  },
  {
    label: "Skills-Heavy",
    weights: { "Leadership & Team Size": 30, "Financial & Impact Metrics": 30, "Experience & Tenure": 40, "Skills & Tools": 90, "Domain & Industry": 60, "Compliance & Certification": 40 },
  },
  {
    label: "Leadership-First",
    weights: { "Leadership & Team Size": 90, "Financial & Impact Metrics": 70, "Experience & Tenure": 60, "Skills & Tools": 40, "Domain & Industry": 40, "Compliance & Certification": 50 },
  },
];

export default {
  name: "AdminManagement",
  data: () => ({
    activeTab: 0,
    error: "",
    loading: false,
    adding: false,
    removing: null,
    newAdminEmail: "",
    adminEmails: [],
    adminRoles: [],
    dataRetentionDays: 365,
    savingRetention: false,
    exportAuditLog: [],
    roleOptions: [
      { text: 'Viewer', value: 'user' },
      { text: 'Admin', value: 'admin' },
      { text: 'Super Admin', value: 'super_admin' },
    ],
    retentionOptions: [
      { text: '30 days', value: 30 },
      { text: '60 days', value: 60 },
      { text: '90 days', value: 90 },
      { text: '180 days', value: 180 },
      { text: '365 days', value: 365 },
    ],
    orgDescription: "",
    overrideInput: "",
    overrideLevel: "",
    showToast: false,
    toastMessage: "",
    newProvider: { name: "", endpoint: "", models: "" },
    cacheStatus: { profileCount: 0, isCacheLoaded: false, lastRefreshedAt: null, cacheAgeMinutes: null },
    cacheRefreshing: false,
    cacheRefreshResult: "",
    dataExportTotalEmployees: 0,
    dataExporting: false,
    themeSaving: false,
    timezoneOptions: [
      { text: 'Eastern (ET)', value: 'Eastern Standard Time' },
      { text: 'Central (CT)', value: 'Central Standard Time' },
      { text: 'Mountain (MT)', value: 'Mountain Standard Time' },
      { text: 'Pacific (PT)', value: 'Pacific Standard Time' },
      { text: 'UTC', value: 'UTC' },
      { text: 'GMT (UK)', value: 'GMT Standard Time' },
      { text: 'Central Europe (CET)', value: 'Central European Standard Time' },
      { text: 'India (IST)', value: 'India Standard Time' },
      { text: 'Australia Eastern (AEST)', value: 'AUS Eastern Standard Time' },
    ],
    allowlistEntries: [],
    allowlistLoading: false,
    allowlistAdding: false,
    allowlistRemoving: null,
    allowlistEntryType: "email",
    allowlistEntryValue: "",
    allowlistEntryReason: "",
    blocklistEntries: [],
    blocklistLoading: false,
    blocklistAdding: false,
    blocklistRemoving: null,
    blocklistEmail: "",
    blocklistDisplayName: "",
    blocklistReason: "",
    deactivatedUsers: [],
    deactivatedLoading: false,
    deactivating: false,
    reactivating: null,
    deactivateEmail: "",
    deactivateDisplayName: "",
    deactivateReason: "",
    auditLogEntries: [],
    auditLogLoading: false,
    auditLogActionFilter: "",
    auditLogTargetFilter: "",
    auditLogStartDate: "",
    auditLogEndDate: "",
    auditActionTypes: [
      { text: "All Actions", value: "" },
      { text: "Add Admin", value: "add_admin" },
      { text: "Remove Admin", value: "remove_admin" },
      { text: "Change Role", value: "set_admin_role" },
      { text: "Add to Allowlist", value: "add_to_allowlist" },
      { text: "Remove from Allowlist", value: "remove_from_allowlist" },
      { text: "Add to Blocklist", value: "add_to_blocklist" },
      { text: "Remove from Blocklist", value: "remove_from_blocklist" },
      { text: "Deactivate User", value: "deactivate_user" },
      { text: "Reactivate User", value: "reactivate_user" },
      { text: "Data Export", value: "data_export" },
      { text: "Save Company Profile", value: "save_company_profile" },
      { text: "Update Scoring Weights", value: "update_scoring_weights" },
    ],
    userDirectoryEntries: [],
    userDirectoryLoading: false,
    userDirectorySearch: "",
    userDirectoryStatusFilter: "",
    userDirectoryRoleFilter: "",
    userDirectoryPage: 1,
    userDirectoryPageSize: 25,
    userDirectoryConflicts: [],
    userDirectoryConflictCount: 0,
    conflictDialog: false,
    conflictDialogUser: {},
    conflictDialogWarnings: [],
    conflictWarningDialog: false,
    pendingConflictWarnings: [],
    pendingConflictAction: null,
    brandingForm: {
      companyName: "",
      loginTagline: "",
      primaryColor: "#007AFF",
      headerColor: "#1E3A5F",
      accentColor: "#F59E0B",
      logoBase64: "",
      faviconBase64: "",
      poweredByVisible: true,
    },
    brandingSaving: false,
    brandingSaveSuccess: false,
    brandingSaveError: "",
    brandingLoaded: false,
    hierarchyOrder: HIERARCHY_ORDER,
    seniorityColors: SENIORITY_COLORS,
    scoringTiers: SCORING_TIERS,
    weightPresets: WEIGHT_PRESETS,
    industryOptions: [
      "Technology", "Healthcare", "Finance", "Manufacturing", "Retail",
      "Education", "Engineering & Construction", "Government", "Consulting",
      "Energy", "Media", "Telecommunications", "Non-Profit", "Other",
    ],
    sizeOptions: [
      "1-50", "51-200", "201-500", "501-1000", "1001-5000",
      "5001-10000", "10000+",
    ],
    regionOptions: [
      "North America", "Europe", "Asia-Pacific", "Latin America",
      "Middle East & Africa", "Global",
    ],
    benchRatioOptions: [
      "1:1", "2:1", "3:1", "4:1", "5:1",
    ],
    industryPresets: [
      { label: "Tech", ratio: "3:1" },
      { label: "Healthcare", ratio: "4:1" },
      { label: "Finance", ratio: "3:1" },
      { label: "Manufacturing", ratio: "2:1" },
      { label: "E&C", ratio: "3:1" },
      { label: "Government", ratio: "5:1" },
    ],
  }),
  computed: {
    ...mapFields("auth", {
      currentUserEmail: "userInfo.email",
    }),
    isSuperAdmin() {
      return this.$store.state.auth.companySuperAdmin;
    },
    adminRolesList() {
      if (this.adminRoles.length > 0) return this.adminRoles;
      return this.adminEmails.map(email => ({ email, role: 'admin' }));
    },
    ...mapState("intelligenceAdmin", {
      configIsLoading: "isLoading",
      configIsSaving: "isSaving",
      configHasError: "hasError",
      configErrorMessage: "errorMessage",
      configSaveSuccess: "saveSuccess",
      hierarchyData: "hierarchyData",
      hierarchyLoading: "hierarchyLoading",
      titleMappings: "titleMappings",
      levelOverrides: "levelOverrides",
      hierarchyAssistantLoading: "hierarchyAssistantLoading",
      scoringWeights: "scoringWeights",
      scoringWeightsLoading: "scoringWeightsLoading",
      scoringWeightsSaving: "scoringWeightsSaving",
      sageHRMode: "sageHRMode",
      sageHRModeLoading: "sageHRModeLoading",
    }),
    ...mapGetters("intelligenceAdmin", ["hasCompanyName", "handlerCategories"]),
    ...mapState("intelligenceAIConfig", {
      aiConfigLoading: "isLoading",
      aiConfigSaving: "isSaving",
      aiConfigSaveSuccess: "saveSuccess",
      aiTestResults: "testResults",
      customProviders: "customProviders",
      aiChangeLog: "changeLog",
      changeLogLoading: "changeLogLoading",
    }),
    ...mapGetters("intelligenceAIConfig", {
      aiFeatures: "allFeatures",
      allAIProviders: "allProviders",
      getFeatureAssignment: "getAssignment",
      aiProviderOptions: "providerOptions",
      getModelInfo: "getModelInfo",
    }),
    companyConfig: {
      get() {
        return this.$store.state.intelligenceAdmin.companyConfig;
      },
      set(val) {
        this.$store.commit("intelligenceAdmin/setCompanyConfig", val);
      },
    },
    lastSyncedDisplay() {
      if (!this.cacheStatus.lastRefreshedAt) return 'Not Yet Synced';
      try {
        const d = new Date(this.cacheStatus.lastRefreshedAt);
        if (isNaN(d.getTime())) return 'Not Yet Synced';
        return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return 'Not Yet Synced';
      }
    },
    cacheAgeDisplay() {
      if (!this.cacheStatus.cacheAgeMinutes && this.cacheStatus.cacheAgeMinutes !== 0) return '—';
      const mins = this.cacheStatus.cacheAgeMinutes;
      if (mins < 1) return 'Just now';
      if (mins < 60) return `${Math.round(mins)} min`;
      if (mins < 1440) return `${Math.round(mins / 60)} hr`;
      return `${Math.round(mins / 1440)} days`;
    },
    cacheAgeColor() {
      const mins = this.cacheStatus.cacheAgeMinutes;
      if (!mins && mins !== 0) return '#9E9E9E';
      if (mins < 60) return '#4CAF50';
      if (mins < 720) return '#FF9800';
      return '#F44336';
    },
    themePreference() {
      return this.$store.state.auth.themePreference || "system";
    },
    filteredUserDirectory() {
      let results = this.userDirectoryEntries;
      if (this.userDirectorySearch) {
        const q = this.userDirectorySearch.toLowerCase();
        results = results.filter(u =>
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q)
        );
      }
      if (this.userDirectoryStatusFilter) {
        results = results.filter(u => u.status === this.userDirectoryStatusFilter);
      }
      if (this.userDirectoryRoleFilter) {
        if (this.userDirectoryRoleFilter === "super_admin") {
          results = results.filter(u => u.isSuperAdmin);
        } else if (this.userDirectoryRoleFilter === "admin") {
          results = results.filter(u => u.isAdmin && !u.isSuperAdmin);
        } else {
          results = results.filter(u => !u.isAdmin && !u.isSuperAdmin);
        }
      }
      return results;
    },
    paginatedUserDirectory() {
      const start = (this.userDirectoryPage - 1) * this.userDirectoryPageSize;
      return this.filteredUserDirectory.slice(start, start + this.userDirectoryPageSize);
    },
  },
  watch: {
    userDirectorySearch() {
      this.userDirectoryPage = 1;
    },
    userDirectoryStatusFilter() {
      this.userDirectoryPage = 1;
    },
    userDirectoryRoleFilter() {
      this.userDirectoryPage = 1;
    },
    activeTab(val) {
      if (val === 1) {
        this.loadAllowlist();
        this.loadBlocklist();
        this.loadDeactivatedUsers();
        this.loadAuditLog();
        this.loadUserDirectory();
      } else if (val === 3) {
        this.loadHierarchy();
        this.loadLevelOverrides();
        this.loadScoringWeights();
      } else if (val === 4) {
        this.loadAIConfig();
        this.loadChangeLog();
      } else if (val === 5) {
        this.loadDataExportStats();
      } else if (val === 6) {
        this.loadBrandingConfig();
      }
    },
  },
  async mounted() {
    if (!this.currentUserEmail) {
      await this.logUserIn();
    }
    this.loadAdmins();
    this.loadCompanyConfig().then(() => {
      if (this.companyConfig && this.companyConfig.dataRetentionDays) {
        this.dataRetentionDays = this.companyConfig.dataRetentionDays;
      }
    });
    this.loadCacheStatus();
    this.loadExportAuditLog();
    this.loadSageHRMode();
  },
  methods: {
    ...mapActions("auth", ["logUserIn"]),
    ...mapActions("intelligenceAdmin", [
      "loadCompanyConfig",
      "saveCompanyConfig",
      "loadHierarchy",
      "loadLevelOverrides",
      "applyHierarchyAssistant",
      "saveLevelOverride",
      "deleteLevelOverride",
      "loadScoringWeights",
      "saveScoringWeights",
      "loadSageHRMode",
      "toggleSageHRMode",
    ]),
    ...mapActions("intelligenceAIConfig", [
      "loadAIConfig",
      "saveAIConfig",
      "testProvider",
      "loadChangeLog",
    ]),
    async handleSageHRModeToggle() {
      await this.toggleSageHRMode();
    },
    async loadCacheStatus() {
      try {
        const result = await adminApi.GetCacheStatus();
        if (result.success) {
          this.cacheStatus = result.data || this.cacheStatus;
        }
      } catch (e) {
        // silent
      }
    },
    async refreshCache() {
      this.cacheRefreshing = true;
      this.cacheRefreshResult = "";
      try {
        const result = await adminApi.RefreshProfileCache();
        if (result.success) {
          this.cacheRefreshResult = result.data?.message || `Refreshed ${result.data?.profileCount} profiles`;
          await this.loadCacheStatus();
        } else {
          this.cacheRefreshResult = "Refresh failed: " + (result.msg || "Unknown error");
        }
      } catch (e) {
        this.cacheRefreshResult = "Refresh failed: " + e.message;
      } finally {
        this.cacheRefreshing = false;
      }
    },
    async loadDataExportStats() {
      try {
        const result = await companyApi.GetEmployeeStats();
        if (result.success) {
          this.dataExportTotalEmployees = result.data?.totalEmployees || 0;
        }
      } catch (e) {
        // silent
      }
    },
    async handleExportProfiles() {
      this.dataExporting = true;
      try {
        const result = await exportAllEmployeeProfiles();
        if (result && result.profileCount > 0) {
          try {
            await adminApi.SaveExportAudit({
              exportType: 'full_profile_export',
              recordCount: result.profileCount,
              adminEmail: this.currentUserEmail,
            });
            await this.loadExportAuditLog();
          } catch (e) {
            // audit logging is best-effort
          }
        }
      } finally {
        this.dataExporting = false;
      }
    },
    async changeRole(email, newRole) {
      try {
        const result = await companyApi.SetAdminRole(email, newRole);
        if (result.success) {
          this.toast(`${email} role changed to ${this.roleLabel(newRole)}`);
          await this.loadAdmins();
        } else {
          this.error = result.msg || 'Failed to change role';
        }
      } catch (e) {
        this.error = 'Failed to change role';
      }
    },
    async saveRetentionPolicy() {
      this.savingRetention = true;
      try {
        const config = { ...this.companyConfig, dataRetentionDays: this.dataRetentionDays };
        this.$store.commit("intelligenceAdmin/setCompanyConfig", config);
        await this.saveCompanyConfig();
        this.toast(`Data retention set to ${this.dataRetentionDays} days`);
      } finally {
        this.savingRetention = false;
      }
    },
    async loadExportAuditLog() {
      try {
        const result = await adminApi.GetExportAuditLog();
        if (result.success) {
          this.exportAuditLog = result.data?.entries || [];
        }
      } catch (e) {
        // silent
      }
    },
    formatAuditDate(ts) {
      if (!ts) return '';
      try {
        const d = new Date(ts);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return ts;
      }
    },
    async handleThemeChange(theme) {
      if (!theme) return;
      this.themeSaving = true;
      themeUtil.setStoredTheme(theme);
      this.$store.commit("auth/setThemePreference", theme);
      try {
        await authApi.SetUserTheme(theme);
      } catch (e) {
        // best-effort persist
      } finally {
        this.themeSaving = false;
      }
    },
    toast(msg) {
      this.toastMessage = msg;
      this.showToast = true;
    },
    roleLabel(role) {
      const labels = { user: "Viewer", admin: "Admin", super_admin: "Super Admin" };
      return labels[role] || "Admin";
    },
    roleChipColor(role) {
      const colors = { user: "#9E9E9E", admin: "#007AFF", super_admin: "#8b5cf6" };
      return colors[role] || "#007AFF";
    },
    isCurrentUser(email) {
      return (
        this.currentUserEmail &&
        email.toLowerCase() === this.currentUserEmail.toLowerCase()
      );
    },
    getLevelTitles(level) {
      return this.titleMappings?.level_titles?.[level] || [];
    },
    getLevelTotalCount(level) {
      const titles = this.getLevelTitles(level);
      return titles.reduce((s, t) => s + (t.count || 0), 0);
    },
    async loadAdmins() {
      this.loading = true;
      this.error = "";
      try {
        const result = await companyApi.GetAdminEmails();
        if (result.success) {
          this.adminEmails = result.data?.adminEmails || [];
          this.adminRoles = result.data?.adminRoles || [];
        } else {
          this.error = "Failed to load admins";
        }
      } finally {
        this.loading = false;
      }
    },
    async addAdmin() {
      if (!this.newAdminEmail) return;
      this.adding = true;
      this.error = "";
      try {
        const result = await companyApi.AddAdminEmail(this.newAdminEmail);
        if (result.success) {
          this.adminEmails = result.data?.adminEmails || [];
          this.newAdminEmail = "";
          await this.logUserIn();
        } else {
          this.error = "Failed to add admin";
        }
      } finally {
        this.adding = false;
      }
    },
    async removeAdmin(email) {
      this.removing = email;
      this.error = "";
      try {
        const result = await companyApi.RemoveAdminEmail(email);
        if (result.success) {
          this.adminEmails = result.data?.adminEmails || [];
          await this.logUserIn();
        } else {
          this.error = "Failed to remove admin";
        }
      } finally {
        this.removing = null;
      }
    },
    saveConfig() {
      this.saveCompanyConfig();
    },
    async handleApplyAssistant() {
      if (!this.orgDescription.trim()) return;
      const result = await this.applyHierarchyAssistant(this.orgDescription);
      if (result && result.success) {
        this.toast("Applied title-to-level mappings");
      } else {
        this.toast("Failed to parse structure");
      }
    },
    async handleAddOverride() {
      if (!this.overrideInput.trim() || !this.overrideLevel) return;
      const result = await this.saveLevelOverride({
        titlePattern: this.overrideInput.trim(),
        assignedLevel: this.overrideLevel,
      });
      if (result && result.success) {
        this.toast(`"${this.overrideInput}" assigned to ${this.overrideLevel}`);
        this.overrideInput = "";
        this.overrideLevel = "";
      }
    },
    async handleReassign(title, newLevel) {
      if (!newLevel) return;
      const result = await this.saveLevelOverride({
        titlePattern: title.toLowerCase(),
        assignedLevel: newLevel,
      });
      if (result && result.success) {
        this.toast(`"${title}" reassigned to ${newLevel}`);
      }
    },
    async handleDeleteOverride(pattern) {
      await this.deleteLevelOverride(pattern);
      this.toast(`Override "${pattern}" removed`);
    },
    updateWeight(name, value) {
      this.$store.commit("intelligenceAdmin/updateScoringWeight", { name, value });
    },
    async handleSaveWeights() {
      const result = await this.saveScoringWeights();
      if (result && result.success) {
        this.toast("Scoring defaults saved");
      } else {
        this.toast("Scoring defaults saved");
      }
    },
    handleResetWeights() {
      this.applyPreset(WEIGHT_PRESETS[0]);
      this.toast("Weights reset to defaults");
    },
    async handleResetAIDefaults() {
      await this.$store.dispatch("intelligenceAIConfig/loadAIConfig");
      this.toast("AI feature assignments reset to server defaults");
    },
    applyPreset(preset) {
      Object.entries(preset.weights).forEach(([name, value]) => {
        this.$store.commit("intelligenceAdmin/updateScoringWeight", { name, value });
      });
    },
    getModelsForFeature(featureKey) {
      const assignment = this.getFeatureAssignment(featureKey);
      return this.$store.getters["intelligenceAIConfig/modelOptionsForProvider"](assignment.providerId);
    },
    getModelMeta(modelId) {
      return this.$store.getters["intelligenceAIConfig/getModelInfo"](modelId);
    },
    getModelDropdownItems(featureKey) {
      const models = this.getModelsForFeature(featureKey);
      return models.map((m) => {
        const info = this.getModelMeta(m);
        return { text: m, value: m, info };
      });
    },
    handleProviderChange(featureKey, providerId) {
      this.$store.commit("intelligenceAIConfig/setFeatureProvider", { featureKey, providerId });
    },
    handleModelChange(featureKey, model) {
      this.$store.commit("intelligenceAIConfig/setFeatureModel", { featureKey, model });
    },
    formatChangeDate(timestamp) {
      if (!timestamp) return "";
      const d = new Date(timestamp);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    getChangeIcon(changeType) {
      const icons = {
        provider_change: { icon: "mdi-swap-horizontal", color: "#3b82f6" },
        model_change: { icon: "mdi-tune", color: "#8b5cf6" },
        config_save: { icon: "mdi-content-save", color: "#22c55e" },
        custom_provider_added: { icon: "mdi-plus-circle", color: "#f59e0b" },
        custom_provider_removed: { icon: "mdi-minus-circle", color: "#ef4444" },
        bulk_change: { icon: "mdi-select-all", color: "#14b8a6" },
      };
      return icons[changeType] || { icon: "mdi-circle-small", color: "#9ca3af" };
    },
    async handleSaveAIConfig() {
      const result = await this.saveAIConfig();
      if (result && result.success) {
        this.toast("AI provider configuration saved");
      } else {
        this.toast("Failed to save AI configuration");
      }
    },
    async handleTestProvider(providerId) {
      await this.testProvider(providerId);
      const result = this.aiTestResults[providerId];
      if (result) {
        this.toast(result.message || result.status);
      }
    },
    handleSetAllProvider(providerId) {
      this.aiFeatures.forEach((feature) => {
        this.$store.commit("intelligenceAIConfig/setFeatureProvider", {
          featureKey: feature.key,
          providerId,
        });
      });
    },
    handleAddCustomProvider() {
      const id = "custom_" + this.newProvider.name.toLowerCase().replace(/\s+/g, "_") + "_" + Date.now();
      const models = this.newProvider.models.split(",").map((m) => m.trim()).filter(Boolean);
      this.$store.commit("intelligenceAIConfig/addCustomProvider", {
        id,
        name: this.newProvider.name.trim(),
        type: "openai-compatible",
        icon: "mdi-cloud-outline",
        color: "#607d8b",
        models,
        defaultModel: models[0] || "",
        supportsToolCalling: true,
        endpoint: this.newProvider.endpoint.trim(),
        isCustom: true,
        configFields: ["apiKey", "endpoint"],
      });
      this.newProvider = { name: "", endpoint: "", models: "" };
      this.toast("Custom provider added");
    },
    handleRemoveCustomProvider(providerId) {
      this.$store.commit("intelligenceAIConfig/removeCustomProvider", providerId);
      this.aiFeatures.forEach((feature) => {
        const assignment = this.getFeatureAssignment(feature.key);
        if (assignment.providerId === providerId) {
          this.$store.commit("intelligenceAIConfig/setFeatureProvider", {
            featureKey: feature.key,
            providerId: "openai",
          });
        }
      });
      this.toast("Custom provider removed");
    },
    async loadAllowlist() {
      this.allowlistLoading = true;
      try {
        const result = await companyApi.GetAllowlist();
        if (result.success) {
          this.allowlistEntries = result.data?.entries || [];
        }
      } catch (e) {
        // silent
      } finally {
        this.allowlistLoading = false;
      }
    },
    async addAllowlistEntry() {
      if (!this.allowlistEntryValue) return;
      this.allowlistAdding = true;
      try {
        const data = { reason: this.allowlistEntryReason };
        if (this.allowlistEntryType === "domain") {
          data.domain = this.allowlistEntryValue;
        } else {
          data.email = this.allowlistEntryValue;
        }
        const result = await companyApi.AddToAllowlist(data);
        if (result.success) {
          this.allowlistEntryValue = "";
          this.allowlistEntryReason = "";
          this.toast("Added to allowlist");
          await this.refreshAllUserManagementData();
        }
      } catch (e) {
        this.toast("Failed to add to allowlist");
      } finally {
        this.allowlistAdding = false;
      }
    },
    async removeAllowlistEntry(entryId) {
      this.allowlistRemoving = entryId;
      try {
        const result = await companyApi.RemoveFromAllowlist(entryId);
        if (result.success) {
          this.toast("Removed from allowlist");
          await this.refreshAllUserManagementData();
        }
      } catch (e) {
        this.toast("Failed to remove from allowlist");
      } finally {
        this.allowlistRemoving = null;
      }
    },
    async loadBlocklist() {
      this.blocklistLoading = true;
      try {
        const result = await companyApi.GetBlocklist();
        if (result.success) {
          this.blocklistEntries = result.data?.entries || [];
        }
      } catch (e) {
        // silent
      } finally {
        this.blocklistLoading = false;
      }
    },
    async addBlocklistEntry() {
      if (!this.blocklistEmail) return;
      const email = this.blocklistEmail;
      const doBlock = async () => {
        this.blocklistAdding = true;
        try {
          const result = await companyApi.AddToBlocklist({
            email: this.blocklistEmail,
            displayName: this.blocklistDisplayName,
            reason: this.blocklistReason,
          });
          if (result.success) {
            this.blocklistEmail = "";
            this.blocklistDisplayName = "";
            this.blocklistReason = "";
            this.toast("User blocked");
            await this.refreshAllUserManagementData();
          }
        } catch (e) {
          this.toast("Failed to block user");
        } finally {
          this.blocklistAdding = false;
        }
      };
      await this.checkConflictsBeforeAction(email, "block", doBlock);
    },
    async removeBlocklistEntry(entryId) {
      this.blocklistRemoving = entryId;
      try {
        const result = await companyApi.RemoveFromBlocklist(entryId);
        if (result.success) {
          this.toast("User unblocked");
          await this.refreshAllUserManagementData();
        }
      } catch (e) {
        this.toast("Failed to unblock user");
      } finally {
        this.blocklistRemoving = null;
      }
    },
    async loadDeactivatedUsers() {
      this.deactivatedLoading = true;
      try {
        const result = await companyApi.GetDeactivatedUsers();
        if (result.success) {
          this.deactivatedUsers = result.data?.entries || [];
        }
      } catch (e) {
        // silent
      } finally {
        this.deactivatedLoading = false;
      }
    },
    async deactivateUserAction() {
      if (!this.deactivateEmail) return;
      const email = this.deactivateEmail;
      const doDeactivate = async () => {
        this.deactivating = true;
        try {
          const result = await companyApi.DeactivateUser({
            email: this.deactivateEmail,
            displayName: this.deactivateDisplayName,
            reason: this.deactivateReason,
          });
          if (result.success) {
            this.deactivateEmail = "";
            this.deactivateDisplayName = "";
            this.deactivateReason = "";
            this.toast("User deactivated");
            await this.refreshAllUserManagementData();
          }
        } catch (e) {
          this.toast("Failed to deactivate user");
        } finally {
          this.deactivating = false;
        }
      };
      await this.checkConflictsBeforeAction(email, "deactivate", doDeactivate);
    },
    async reactivateUserAction(entryId) {
      this.reactivating = entryId;
      try {
        const result = await companyApi.ReactivateUser(entryId);
        if (result.success) {
          this.toast("User reactivated");
          await this.refreshAllUserManagementData();
        }
      } catch (e) {
        this.toast("Failed to reactivate user");
      } finally {
        this.reactivating = null;
      }
    },
    async loadAuditLog() {
      this.auditLogLoading = true;
      try {
        const startIso = this.auditLogStartDate ? new Date(this.auditLogStartDate).toISOString() : null;
        const endIso = this.auditLogEndDate ? new Date(this.auditLogEndDate + "T23:59:59").toISOString() : null;
        const result = await companyApi.GetAdminAuditLog(
          this.auditLogActionFilter,
          this.auditLogTargetFilter,
          200,
          startIso,
          endIso
        );
        if (result.success) {
          this.auditLogEntries = result.data?.entries || [];
        }
      } catch (e) {
        // silent
      } finally {
        this.auditLogLoading = false;
      }
    },
    getAuditActionColor(actionType) {
      const colors = {
        add_admin: "#22c55e",
        remove_admin: "#ef4444",
        set_admin_role: "#3b82f6",
        add_to_allowlist: "#007AFF",
        remove_from_allowlist: "#f59e0b",
        add_to_blocklist: "#ef4444",
        remove_from_blocklist: "#22c55e",
        deactivate_user: "#f59e0b",
        reactivate_user: "#22c55e",
        data_export: "#8b5cf6",
        save_company_profile: "#6366f1",
        update_scoring_weights: "#6366f1",
      };
      return colors[actionType] || "#9ca3af";
    },
    formatAuditAction(actionType) {
      const labels = {
        add_admin: "Add Admin",
        remove_admin: "Remove Admin",
        set_admin_role: "Change Role",
        add_to_allowlist: "Allowlist Add",
        remove_from_allowlist: "Allowlist Remove",
        add_to_blocklist: "Block User",
        remove_from_blocklist: "Unblock User",
        deactivate_user: "Deactivate",
        reactivate_user: "Reactivate",
        data_export: "Data Export",
        save_company_profile: "Config Update",
        update_scoring_weights: "Weights Update",
      };
      return labels[actionType] || actionType;
    },
    async loadUserDirectory() {
      this.userDirectoryLoading = true;
      try {
        const result = await companyApi.GetAllUsersWithStatus();
        if (result.success) {
          this.userDirectoryEntries = result.data?.users || [];
          this.userDirectoryConflicts = result.data?.conflicts || [];
          this.userDirectoryConflictCount = result.data?.conflictCount || 0;
        }
      } catch (e) {
        // silent
      } finally {
        this.userDirectoryLoading = false;
      }
    },
    showUserConflictDetail(user) {
      this.conflictDialogUser = user;
      this.conflictDialogWarnings = (user.conflicts || []).map(c => this.formatConflictWarning(c));
      this.conflictDialog = true;
    },
    formatConflictType(conflict) {
      const labels = {
        blocked_but_allowlisted: "Blocked + Allowlisted",
        deactivated_but_allowlisted: "Deactivated + Allowlisted",
        blocked_and_deactivated: "Blocked + Deactivated",
      };
      return labels[conflict] || conflict;
    },
    formatConflictWarning(conflict) {
      const warnings = {
        blocked_but_allowlisted: "This user is on the allowlist but also blocked. Blocklist takes precedence — they cannot log in.",
        deactivated_but_allowlisted: "This user is on the allowlist but also deactivated. Deactivation takes precedence — they cannot log in.",
        blocked_and_deactivated: "This user is both blocked and deactivated. Both must be removed for access to be restored.",
      };
      return warnings[conflict] || conflict;
    },
    async checkConflictsBeforeAction(email, actionType, actionFn) {
      try {
        const result = await companyApi.CheckUserConflicts(email, actionType);
        if (result.success && result.data?.hasConflicts) {
          this.pendingConflictWarnings = result.data.warnings;
          this.pendingConflictAction = actionFn;
          this.conflictWarningDialog = true;
          return;
        }
      } catch (e) {
        // proceed without conflict check
      }
      await actionFn();
    },
    async confirmConflictAction() {
      this.conflictWarningDialog = false;
      if (this.pendingConflictAction) {
        await this.pendingConflictAction();
      }
      this.pendingConflictAction = null;
      this.pendingConflictWarnings = [];
    },
    cancelConflictAction() {
      this.conflictWarningDialog = false;
      this.pendingConflictAction = null;
      this.pendingConflictWarnings = [];
    },
    async refreshAllUserManagementData() {
      await Promise.all([
        this.loadAllowlist(),
        this.loadBlocklist(),
        this.loadDeactivatedUsers(),
        this.loadAuditLog(),
        this.loadUserDirectory(),
      ]);
    },
    async loadBrandingConfig() {
      if (this.brandingLoaded) return;
      try {
        const response = await brandingApi.GetBrandingConfig();
        if (response?.success && response.data) {
          this.brandingForm = {
            companyName: response.data.companyName || "",
            loginTagline: response.data.loginTagline || "",
            primaryColor: response.data.primaryColor || "#007AFF",
            headerColor: response.data.headerColor || "#1E3A5F",
            accentColor: response.data.accentColor || "#F59E0B",
            logoBase64: response.data.logoBase64 || "",
            faviconBase64: response.data.faviconBase64 || "",
            poweredByVisible: response.data.poweredByVisible !== false,
          };
        }
        this.brandingLoaded = true;
      } catch (e) {
        console.warn("Failed to load branding config:", e);
      }
    },
    async saveBranding() {
      this.brandingSaving = true;
      this.brandingSaveSuccess = false;
      this.brandingSaveError = "";
      try {
        const response = await brandingApi.SaveBrandingConfig(this.brandingForm);
        if (response?.success) {
          this.brandingSaveSuccess = true;
          this.$store.commit("branding/SET_CONFIG", { ...this.brandingForm });
          setTimeout(() => { this.brandingSaveSuccess = false; }, 4000);
        } else {
          this.brandingSaveError = response?.displayText || "Failed to save branding";
        }
      } catch (e) {
        this.brandingSaveError = "Failed to save branding configuration";
      } finally {
        this.brandingSaving = false;
      }
    },
    resetBrandingForm() {
      this.brandingForm = {
        companyName: "",
        loginTagline: "",
        primaryColor: "#007AFF",
        headerColor: "#1E3A5F",
        accentColor: "#F59E0B",
        logoBase64: "",
        faviconBase64: "",
        poweredByVisible: true,
      };
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.size > 500 * 1024) {
        this.brandingSaveError = "Logo file must be under 500KB";
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.brandingForm.logoBase64 = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    handleFaviconUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.size > 100 * 1024) {
        this.brandingSaveError = "Favicon file must be under 100KB";
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.brandingForm.faviconBase64 = e.target.result;
      };
      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped>
.icon-circle {
  color: white;
  background-color: #0059a3;
  border-radius: 50%;
  padding: 10px;
}

.headline-text {
  font-size: 24px;
  font-weight: 500;
}

.simpleCard {
  border-radius: 10px;
  border: 1px solid var(--resu-border, #e0e0e0);
}

.level-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.model-info-selected {
  background-color: rgba(0, 122, 255, 0.08);
  border-color: #007AFF !important;
}
</style>
